import { useEffect, useState } from 'react';
import { settingControllerKey } from '../../../Player/PlayerModels';
import { CustomOption, CustomOptionWrapper, SelectingItemMainWrapper, SettingPanelMultiSelectItem } from '../../../Player/styles';
import { PanelSettingHeader, PanelSettingLayer } from '../../../Player/styles/Panel';
import { IconCaption } from '../../../../icons';
import UsePlayerProps from '../../../../Store/usePlayerProps';
import { Box } from '../../../../styles';

const ControlBarCaptionPanel = ({ player, changePosition, handleSettingItemClick, activeSetting }:
  {
    player: HTMLVideoElement,
    changePosition: boolean,
    handleSettingItemClick: (controllerKey: settingControllerKey) => void
    activeSetting: string | null
  }) => {
  const [isOpen, setIsOpen] = useState(false);
  const playerState = UsePlayerProps((state) => state.playerState);
  const { captionState } = playerState;
  const cueDiv = document.getElementById('custom-track');
  const captions = playerState.sources?.captionFiles;
  const setPlayerState = UsePlayerProps((state) => state.setPlayerState);

  useEffect(() => {
    setAvailableCaptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  useEffect(() => {
    // Reset every track first so only the selected language renders into #custom-track.
    // Otherwise a previously selected track stays in 'hidden' mode with its oncuechange
    // still firing, and two languages fight over the same overlay — most visible on Safari/
    // Mac, where the native cue display is hidden so the custom overlay is the only renderer.
    Array.from(player.textTracks).forEach((t) => {
      t.oncuechange = null;
      t.mode = 'disabled';
    });
    if (cueDiv) {
      cueDiv.innerHTML = '';
    }
    if (captionState.selectedCaptionLang.lang === 'off') {
      return;
    }
    // Resolve the track by its position in captionFiles rather than by textTrack.language:
    // Safari normalizes/omits the srclang value for some language codes, which made the match
    // fail and silently fall back to the first (English) track.
    const nonOff = (captions || []).filter((c) => c.language !== 'off');
    let index = nonOff.findIndex((c) => c.language === captionState.selectedCaptionLang.lang);
    if (index < 0) {
      index = Array.from(player.textTracks).findIndex((t) => t.language === captionState.selectedCaptionLang.lang);
    }
    const track = player.textTracks[index];
    if (track) {
      track.mode = 'hidden';
      track.oncuechange = () => {
        const activeCue: any = track.activeCues ? track?.activeCues[0] : player.textTracks[0];

        if (activeCue) {
          const lines = activeCue.text.split('\n');
          const htmlLines = lines.map((line): string => `<p id="custom-track-inner"><span>${line}</span></p>`);
          if (cueDiv) {
            cueDiv.innerHTML = htmlLines.join('');
            cueDiv.style.direction = captionState.selectedCaptionLang.lang === 'eng' ? 'ltr' : 'rtl';
          }
        } else {
          if (cueDiv) {
            cueDiv.innerHTML = '';
          }
        }
      };
      if (cueDiv) {
        cueDiv.style.bottom = changePosition ? '16%' : '5%';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePosition, captionState.selectedCaptionLang.lang]);

  const setAvailableCaptions = () => {
    const selectedTrack = captions && captions.find((track) => track.default);
    const selectedTrackIndex = captions && captions.findIndex((track) => track.default);
    if (selectedTrack) {
      setPlayerState({
        ...playerState,
        captionState: {
          prevCaptionIndex: selectedTrackIndex || -1,
          selectedCaptionLang: {
            lang: selectedTrack.language,
            label: selectedTrack.label,
          },
        },
      });
    }
  };

  const toggleMenu = (e, key) => {
    e.stopPropagation();
    handleSettingItemClick(key);
    setIsOpen(!isOpen);
  };

  const handleCaptionChange = (lang: string, label: string) => {
    const nonOff = (captions || []).filter((c) => c.language !== 'off');
    const index = lang === 'off' ? -1 : nonOff.findIndex((c) => c.language === lang);
    // 'hidden' (not 'showing') keeps cues flowing to oncuechange without the browser also
    // rendering them natively; the lang-change effect re-applies the active track too.
    Array.from(player.textTracks).forEach((t, i) => {
      t.mode = i === index ? 'hidden' : 'disabled';
    });
    setPlayerState({
      ...playerState,
      captionState: {
        prevCaptionIndex: index,
        selectedCaptionLang: {
          lang: lang === 'off' ? 'off' : lang,
          label: lang === 'off' ? 'خاموش' : label,
        },
      },
    });
  };

  return (
    <>
      {!activeSetting &&
        <SettingPanelMultiSelectItem icon={<IconCaption />} label="player.caption" onClick={(e) => toggleMenu(e, 'caption')}
          value={captionState.selectedCaptionLang.label as string} />}
      {activeSetting === 'caption' && <PanelSettingLayer>
        <CaptionList
          returnAction={(e) => toggleMenu(e, null)}
          selectedValue={captionState.selectedCaptionLang.lang}
          list={captions}
          onChange={(lang: string, label: string) => {
            if (lang !== captionState.selectedCaptionLang.lang) {
              handleCaptionChange(lang, label);
            }
            setIsOpen(false);
          }} />
      </PanelSettingLayer>}
    </>
  );
};

export default ControlBarCaptionPanel;
const CaptionList = ({ selectedValue, list, onChange, returnAction }) => (
  <Box>
    <PanelSettingHeader label={'player.caption'} handleClick={(e) => returnAction(e)} />
    <SelectingItemMainWrapper>
      {list.map(({ language, label }) => (<CustomOptionWrapper key={language} onClick={(e) => {
        onChange(language, label);
        returnAction(e);
      }}>
        <CustomOption selected={language === selectedValue}>
          {label}
        </CustomOption>
      </CustomOptionWrapper>))}
    </SelectingItemMainWrapper>
  </Box>
);
